// O que cada coisa significa, em português de família.
//
// POR QUE ISTO EXISTE: a ficha cheia de "PCR 13,36" e "Klebsiella pneumoniae >
// 100.000 UFC/mL" serve ao médico, que já tem isso no prontuário. Para o filho
// que está no quarto, um número sem tradução não é informação — é angústia.
//
// DUAS REGRAS DURAS, e elas mandam em cada palavra escrita aqui:
//
// 1. EXPLICA, NÃO DIAGNOSTICA. Cada texto diz o que a medida É e o que ela
//    costuma significar EM GERAL. Nunca o que está acontecendo com a paciente:
//    isso depende do exame físico, do contexto e do olho de quem está cuidando.
// 2. TERMINA EM PERGUNTA, NÃO EM CONCLUSÃO. O objetivo é a família chegar na
//    visita sabendo o que perguntar — não chegar achando que já sabe a resposta.
//
// Este glossário é FIXO, escrito à mão, e não passa por LLM nenhuma: é
// informação de livro-texto, igual para todo mundo, e não precisa (nem deve)
// depender de chave de API, de internet ou de mandar dado para fora.

import { acharMarcador, formatarValor } from './marcadores.ts';

export interface Verbete {
  /** O que é, em uma ou duas frases, sem jargão. */
  oQueE: string;
  /** Por que a equipe acompanha isso. */
  porQueImporta?: string;
  /** O que valor acima da faixa costuma significar EM GERAL. */
  quandoAlto?: string;
  /** O que valor abaixo da faixa costuma significar EM GERAL. */
  quandoBaixo?: string;
  /** Uma pergunta pronta para a visita médica. */
  pergunta?: string;
  /**
   * O que quem chega precisa saber ANTES de encostar na pessoa.
   *
   * Nasceu de uma cena real: médico entra no quarto sem saber que ela está com
   * marca-passo. O dado estava no relatório, numa tabela da terceira página —
   * verdadeiro e inútil. Isto sobe para o alto do documento, com destaque, e
   * só existe para o punhado de coisas em que não saber muda a conduta.
   */
  alerta?: string;
  /**
   * Quanto o alerta pesa. Decide a ordem e a cor, e existe para o vermelho não
   * virar papel de parede: se tudo grita, nada é ouvido. `critico` é para o que
   * pode levar a uma conduta perigosa — pedir um exame contraindicado. `cuidado`
   * é para o que muda o trato do dia a dia, como não dar água pela boca.
   */
  gravidade?: 'critico' | 'cuidado';
  /**
   * O que fazer com o alerta: procedimentos que mudam por causa dele.
   *
   * Isto não é conduta, é característica do APARELHO — a mesma coisa que vem
   * escrita no manual dele. Quem decide se faz, com qual protocolo e com qual
   * risco continua sendo a equipe. Está aqui porque o médico que chega precisa
   * saber ANTES de pedir: pedir uma ressonância que depois se descobre
   * contraindicada custa dias, e dias aqui têm preço.
   */
  restricoes?: { o: string; porque: string }[];
  /** O que NÃO muda — para o alerta não virar desculpa de adiar exame comum. */
  liberado?: string;
}

// --------------------------------------------------------------- laboratório

export const VERBETES_MARCADOR: Record<string, Verbete> = {
  hcm: {
    oQueE: 'A quantidade média de hemoglobina dentro de cada glóbulo vermelho.',
    porQueImporta: 'Junto com o VCM, aponta o caminho da investigação da anemia: célula pequena e pálida sugere uma causa, célula grande sugere outra.',
    pergunta: 'O que o tamanho e a cor das células dizem sobre a causa da anemia dela?',
  },
  chcm: {
    oQueE: 'A concentração de hemoglobina dentro das células vermelhas.',
    porQueImporta: 'Completa o retrato da série vermelha, junto com o VCM e o HCM.',
    pergunta: 'O que o tamanho e a cor das células dizem sobre a causa da anemia dela?',
  },
  vpm: {
    oQueE: 'O tamanho médio das plaquetas.',
    porQueImporta: 'Plaquetas maiores costumam ser mais novas — o que sugere que a medula está produzindo. É um detalhe que acompanha a contagem, não substitui.',
    pergunta: 'Isso diz alguma coisa sobre a produção de plaquetas dela?',
  },
  segmentados: {
    oQueE: 'O tipo de glóbulo branco que responde primeiro a infecções bacterianas.',
    porQueImporta: 'É a parte do hemograma que mais se mexe em infecção. Subir muito sugere reação a alguma coisa; cair demais significa defesa enfraquecida.',
    quandoAlto: 'Costuma acompanhar infecção bacteriana ou inflamação intensa.',
    quandoBaixo: 'Defesa reduzida contra bactérias; pode vir de infecção muito grave ou de efeito de medicação.',
    pergunta: 'Os neutrófilos estão subindo ou caindo desde que o antibiótico começou?',
  },
  linfocitos: {
    oQueE: 'Glóbulos brancos ligados à defesa contra vírus e à memória do sistema imune.',
    porQueImporta: 'Costumam cair em infecção grave e em estresse do corpo, e voltar a subir na recuperação.',
    quandoBaixo: 'Pode acompanhar infecção grave, uso de corticoide ou desnutrição.',
    quandoAlto: 'Mais comum em infecções por vírus.',
    pergunta: 'O hemograma dela está caminhando para a recuperação?',
  },
  eosinofilos: {
    oQueE: 'Um tipo de glóbulo branco ligado a alergias e a parasitas.',
    porQueImporta: 'Em internação, costuma chamar atenção quando sobe junto de um remédio novo — é um dos sinais de reação alérgica a medicamento.',
    quandoAlto: 'Pode acompanhar reação alérgica, inclusive a algum medicamento em uso.',
    quandoBaixo: 'Comum em quadros agudos e com uso de corticoide.',
    pergunta: 'Este valor pode ter relação com algum dos remédios que ela recebe?',
  },
  basofilos: {
    oQueE: 'O tipo mais raro de glóbulo branco, ligado a processos alérgicos.',
    porQueImporta: 'Aparece em número muito pequeno no hemograma; variações pequenas raramente significam alguma coisa sozinhas.',
    pergunta: 'Isso tem alguma importância no caso dela?',
  },
  po2_venoso: {
    oQueE: 'O oxigênio medido no sangue venoso — o que volta dos tecidos depois de entregar oxigênio.',
    porQueImporta:
      'Não se lê com a régua do sangue arterial. No sangue venoso o valor é naturalmente muito mais baixo, '
      + 'porque o oxigênio já foi usado pelo corpo. Serve para avaliar a extração de oxigênio pelos tecidos, '
      + 'não a oxigenação do pulmão.',
    pergunta: 'O que a gasometria venosa diz sobre a circulação e a oxigenação dela?',
  },
  saturacao_venosa: {
    oQueE: 'Quanto de oxigênio ainda resta na hemoglobina do sangue venoso, depois de passar pelos tecidos.',
    porQueImporta: 'Mostra o equilíbrio entre o que o corpo recebe e o que ele consome de oxigênio. Também não se lê com a régua do sangue arterial.',
    quandoBaixo: 'Os tecidos estão extraindo mais oxigênio que o normal — sinal de que a entrega pode estar insuficiente.',
    pergunta: 'O que a gasometria venosa diz sobre a circulação e a oxigenação dela?',
  },
  anion_gap: {
    oQueE: 'Uma conta feita com os sais do sangue, que ajuda a descobrir a origem de um desequilíbrio ácido-base.',
    porQueImporta: 'Quando o sangue está mais ácido, é este número que separa as causas possíveis — e o caminho da investigação muda conforme a resposta.',
    quandoAlto: 'Sugere acúmulo de ácidos no sangue; a causa exige investigação da equipe.',
    pergunta: 'Este resultado indica algum desequilíbrio que precise ser corrigido?',
  },
  capacidade_livre_ferro: {
    oQueE: 'Quanto ainda sobra de capacidade livre para transportar ferro no sangue.',
    porQueImporta: 'Faz parte do mesmo painel do ferro e da ferritina, e só significa alguma coisa lido com eles.',
    pergunta: 'O que este conjunto (ferro, ferritina, saturação e capacidade de transporte) indica sobre a anemia dela?',
  },
  procalcitonina: {
    oQueE: 'Uma substância que o corpo produz em quantidade bem maior quando a infecção é causada por bactéria.',
    porQueImporta:
      'É o exame que mais ajuda a decidir sobre antibiótico. A PCR sobe em qualquer inflamação — cirurgia, ' +
      'trauma, infecção por vírus —, então ela sozinha não diz se ainda há bactéria. A procalcitonina sobe ' +
      'principalmente com bactéria, e cair é um dos sinais que a equipe usa para pensar em encerrar o antibiótico.',
    quandoBaixo: 'Valor baixo torna infecção bacteriana ativa menos provável — mas quem decide sobre o antibiótico é a equipe, olhando também o exame físico e o resto.',
    quandoAlto: 'Sugere infecção bacteriana em curso, e quanto mais alto, mais provável que seja grave.',
    pergunta: 'A procalcitonina está caindo? Isso muda alguma coisa no tempo de antibiótico?',
  },
  probnp: {
    oQueE: 'Uma substância que o coração libera quando está trabalhando sob pressão, com as câmaras esticadas.',
    porQueImporta:
      'É o número que acompanha a insuficiência cardíaca ao longo do tempo. Sobe quando o coração está ' +
      'sobrecarregado e tende a cair quando o tratamento alivia essa sobrecarga. Em pessoa idosa e com o rim ' +
      'funcionando abaixo do normal ele já costuma vir alto por si — por isso o que interessa é a direção ' +
      'entre um exame e o seguinte, não o número isolado.',
    quandoAlto:
      'Indica coração trabalhando sob pressão. Valores muito altos costumam acompanhar acúmulo de líquido — '
      + 'falta de ar, cansaço aos mínimos esforços, inchaço nas pernas, ganho rápido de peso. Mas o número não '
      + 'se lê isolado: em pessoa idosa, com o rim filtrando abaixo do normal e com o coração já doente, ele '
      + 'costuma vir alto de base, e isso não quer dizer piora. O que diz alguma coisa é a DIREÇÃO entre um '
      + 'exame e o seguinte, e como ela está passando.',
    pergunta:
      'Este valor está melhor ou pior que o anterior? Ele muda alguma coisa no tratamento do coração ou na '
      + 'quantidade de líquido que ela recebe? E o que devemos observar e avisar — falta de ar, inchaço, '
      + 'ganho de peso?',
  },
  // Os que faltavam, todos alterados no exame dela.
  calcio_ionico: {
    oQueE: 'A parte do cálcio que está livre no sangue e de fato ativa — a que o coração e os músculos usam.',
    porQueImporta: 'Diferente do cálcio total, não depende da albumina, e por isso costuma refletir melhor a situação real em quem está internado.',
    quandoBaixo: 'Pouco cálcio ativo disponível; em valores baixos pode afetar o ritmo do coração e a força muscular.',
    pergunta: 'O cálcio iônico baixo precisa de reposição?',
  },
  capacidade_total_ferro: {
    oQueE: 'Quanto ferro o sangue teria capacidade de transportar se estivesse todo ocupado.',
    porQueImporta:
      'Lida junto com a ferritina, ajuda a separar as causas da anemia: quando falta ferro de verdade, esta '
      + 'capacidade costuma AUMENTAR, porque o corpo fabrica mais transportador. Quando ela está baixa com a '
      + 'ferritina alta, o quadro aponta para outro caminho, e é a equipe quem interpreta.',
    quandoBaixo: 'Menos capacidade de transporte de ferro do que o esperado.',
    pergunta: 'O que este conjunto (ferro, ferritina, saturação e capacidade de transporte) indica sobre a anemia dela?',
  },
  monocitos: {
    oQueE: 'Um tipo de glóbulo branco, parte da defesa do corpo.',
    porQueImporta: 'Sobem em infecções prolongadas e durante a recuperação de uma inflamação — por isso o número sozinho diz pouco, e o que se olha é a série ao longo dos dias.',
    quandoAlto: 'Pode acompanhar infecção em curso ou a fase de recuperação de uma.',
    pergunta: 'Os monócitos altos indicam alguma coisa ativa, ou é a recuperação da infecção que ela teve?',
  },
  tap_tempo: {
    oQueE: 'Quanto tempo o sangue leva para coagular, em segundos.',
    porQueImporta: 'Acompanha o INR e a atividade de protrombina. Em quem usa anticoagulante, esses três números são lidos juntos.',
    quandoAlto: 'O sangue está demorando mais que o normal para coagular.',
    pergunta: 'Estes valores de coagulação estão onde deveriam para o anticoagulante que ela usa?',
  },
  tap_atividade: {
    oQueE: 'A eficiência da coagulação, em porcentagem do esperado.',
    porQueImporta: 'É a outra face do tempo de protrombina: quanto menor a atividade, mais lenta a coagulação.',
    quandoBaixo: 'Coagulação mais lenta que o normal, o que aumenta o risco de sangramento.',
    pergunta: 'Estes valores de coagulação estão onde deveriam para o anticoagulante que ela usa?',
  },
  ferritina: {
    oQueE: 'Mede o ferro guardado em estoque no corpo.',
    porQueImporta:
      'É o exame que separa os dois tipos mais comuns de anemia. Se o estoque está vazio, falta matéria-prima. ' +
      'Se o estoque está cheio e mesmo assim falta ferro circulando, o corpo costuma estar retendo o ferro — ' +
      'o que acontece em inflamação e infecção prolongadas. A ferritina também sobe por inflamação em si, ' +
      'então ela não é lida sozinha: vai junto com o ferro e com a saturação de transferrina.',
    quandoAlto: 'Estoque cheio, ou inflamação em curso — as duas causas elevam este número.',
    quandoBaixo: 'Estoque de ferro esgotado.',
    pergunta: 'O que este conjunto (ferro, ferritina e saturação) indica sobre a causa da anemia dela? Isso muda alguma coisa no tratamento?',
  },
  ferro_serico: {
    oQueE: 'O ferro circulando no sangue naquele momento.',
    porQueImporta: 'Varia bastante ao longo do dia e com a alimentação, por isso é sempre lido junto com a ferritina e a saturação de transferrina, nunca sozinho.',
    quandoBaixo: 'Pouco ferro disponível no sangue na hora da coleta.',
    pergunta: 'O que este conjunto (ferro, ferritina e saturação) indica sobre a causa da anemia? Isso muda o tratamento?',
  },
  saturacao_transferrina: {
    oQueE: 'Quanto da capacidade de transporte de ferro do sangue está de fato ocupada.',
    porQueImporta: 'Junta o ferro e a capacidade de transporte num número só, e por isso costuma refletir melhor a disponibilidade real de ferro do que os dois separados.',
  },
  tsh: {
    oQueE: 'O hormônio com que o cérebro manda a tireoide trabalhar. Quando a tireoide produz pouco, ele sobe, como quem aperta mais a campainha.',
    porQueImporta: 'Tireoide lenta dá sono, lentidão, frio, intestino preso, sódio baixo e respiração mais fraca. A amiodarona, remédio do coração, é rica em iodo e é uma causa conhecida. Na saída de uma doença grave o TSH também pode subir por um tempo e voltar sozinho — por isso ele se confirma repetindo.',
    quandoAlto: 'Sugere tireoide produzindo menos do que o corpo pede; confirma-se repetindo junto com o T4 livre.',
    quandoBaixo: 'Sugere tireoide produzindo demais, ou efeito de remédio.',
    pergunta: 'O TSH veio alto. Vale repetir TSH e T4 livre, já que ela usou amiodarona?',
  },
  t4_livre: {
    oQueE: 'O hormônio que a tireoide de fato produz, na forma que age no corpo.',
    porQueImporta: 'É lido junto com o TSH: TSH alto com T4 livre baixo ou no limite é o padrão de tireoide lenta.',
    quandoBaixo: 'Junto com TSH alto, aponta tireoide produzindo pouco.',
    quandoAlto: 'Junto com TSH baixo, aponta tireoide produzindo demais.',
    pergunta: 'O T4 livre no limite, com TSH alto, pede tratamento ou só repetir?',
  },
  vitamina_b12: {
    oQueE: 'Uma vitamina necessária para fabricar glóbulos vermelhos e para o funcionamento dos nervos.',
    porQueImporta: 'A falta dela causa um tipo específico de anemia, com células maiores que o normal. Em quem recebe reposição ou nutrição por sonda, o valor costuma vir alto — o que em geral só reflete o que está sendo ofertado.',
    quandoBaixo: 'Pode explicar anemia e sintomas nos nervos.',
    pergunta: 'A B12 alta é da nutrição que ela recebe pela sonda, ou precisa de investigação?',
  },
  vitamina_d: {
    oQueE: 'Vitamina ligada à saúde dos ossos e à absorção de cálcio.',
    porQueImporta: 'Em pessoa idosa e internada há muito tempo, sem sol, costuma cair. A faixa considerada adequada acima dos 60 anos é mais alta do que a da população geral.',
  },
  hemacias: {
    oQueE: 'A contagem dos glóbulos vermelhos, as células que carregam a hemoglobina.',
    porQueImporta: 'Junto com a hemoglobina, mostra se a anemia é por falta de células ou por células com pouca hemoglobina dentro.',
    quandoBaixo: 'Poucas células vermelhas circulando. É a contrapartida da anemia vista na hemoglobina.',
    pergunta: 'A queda das células vermelhas está sendo acompanhada? Sabe-se de onde vem?',
  },
  rdw: {
    oQueE: 'Mede o quanto os glóbulos vermelhos variam de tamanho entre si.',
    porQueImporta:
      'Quando está alto, quer dizer que há células de tamanhos bem diferentes no mesmo sangue — o que costuma ' +
      'acontecer quando o corpo está produzindo células novas depressa, ou quando falta algo para fabricá-las direito.',
    quandoAlto: 'Células de tamanhos muito diferentes. Ajuda a equipe a investigar a causa da anemia.',
    pergunta: 'O que a variação de tamanho das células diz sobre a causa da anemia dela?',
  },
  vcm: {
    oQueE: 'O tamanho médio dos glóbulos vermelhos.',
    porQueImporta: 'Aponta o caminho da investigação da anemia: células pequenas e células grandes têm causas diferentes.',
  },
  cloretos: {
    oQueE: 'Um sal do sangue, que anda junto com o sódio.',
    porQueImporta: 'Entra na conta do equilíbrio entre ácido e base do corpo, junto com o sódio e o bicarbonato.',
  },
  hemoglobina: {
    oQueE: 'A substância dos glóbulos vermelhos que carrega oxigênio pelo corpo.',
    porQueImporta: 'É o que diz se o sangue está conseguindo levar oxigênio aos órgãos.',
    quandoBaixo: 'Chama-se anemia. Pode vir de perda de sangue, de doença prolongada, de falta de ferro ou das próprias coletas frequentes em internação longa.',
    quandoAlto: 'Menos comum em internação; pode aparecer em desidratação.',
    pergunta: 'A anemia dela está sendo acompanhada? Há previsão de transfusão ou de investigar a causa?',
  },
  hematocrito: {
    oQueE: 'A porcentagem do sangue que é formada por glóbulos vermelhos.',
    porQueImporta: 'Anda junto da hemoglobina e ajuda a confirmar anemia.',
    pergunta: 'A anemia dela está sendo acompanhada? Há previsão de transfusão ou de investigar a causa?',
  },
  leucocitos: {
    oQueE: 'As células de defesa do corpo — o "exército" contra infecção.',
    porQueImporta: 'É um dos primeiros números que a equipe olha quando suspeita de infecção.',
    quandoAlto: 'Costuma indicar que o corpo está reagindo a alguma coisa, com frequência uma infecção. Alguns remédios, como corticoide, também sobem esse número.',
    quandoBaixo: 'Pode significar defesa enfraquecida, por infecção muito grave ou por efeito de medicação.',
    pergunta: 'Os leucócitos estão subindo ou caindo desde que o antibiótico começou?',
  },
  plaquetas: {
    oQueE: 'As células que fazem o sangue coagular e estancam sangramento.',
    quandoBaixo: 'Aumenta o risco de sangramento. Pode cair por infecção grave ou por efeito de remédio.',
    quandoAlto: 'Costuma acompanhar inflamação em curso.',
    pergunta: 'A contagem de plaquetas dela permite manter o anticoagulante com segurança?',
  },
  pcr: {
    oQueE: 'Proteína C reativa: uma substância que o fígado produz quando há inflamação no corpo.',
    porQueImporta: 'Sobe rápido quando há infecção e desce quando o tratamento está funcionando. Por isso é repetida tantas vezes.',
    quandoAlto: 'Indica inflamação ativa em algum lugar. Não diz ONDE nem por quê — infecção, cirurgia recente e outras causas sobem igual.',
    pergunta: 'A PCR está caindo desde a troca do antibiótico? Se não estiver, o que isso muda na conduta?',
  },
  ureia: {
    oQueE: 'Um resíduo que o corpo produz e o rim elimina pela urina.',
    porQueImporta: 'Quando o rim não filtra bem, esse resíduo se acumula no sangue.',
    quandoAlto: 'Pode indicar que o rim está trabalhando mal, ou desidratação, ou sangramento no aparelho digestivo.',
    pergunta: 'A ureia subiu enquanto a creatinina melhorou. O que isso indica — desidratação, a dieta, ou outra coisa?',
  },
  creatinina: {
    oQueE: 'Outro resíduo filtrado pelo rim — o mais usado para medir se o rim está funcionando.',
    porQueImporta: 'É o número que mais pesa na decisão sobre dose de remédio, contraste de exame e necessidade de diálise.',
    quandoAlto: 'Sugere que o rim está filtrando menos do que deveria. Muitos remédios precisam ter a dose ajustada quando isso acontece.',
    pergunta: 'A função renal dela melhorou ou piorou esta semana? As doses dos remédios foram ajustadas por causa disso?',
  },
  tfg: {
    oQueE: 'Taxa de filtração glomerular: uma estimativa de quanto sangue o rim consegue limpar por minuto.',
    porQueImporta: 'Traduz a creatinina em algo mais direto — quanto do rim ainda está trabalhando.',
    quandoBaixo: 'Quanto menor, menos o rim filtra.',
    pergunta: 'A filtração do rim melhorou ou piorou? As doses dos remédios foram ajustadas por causa disso?',
  },
  clearance_creatinina: {
    oQueE: 'Uma medida parecida com a TFG, de quanto o rim consegue limpar.',
    quandoBaixo: 'Rim filtrando abaixo do esperado.',
    pergunta: 'A filtração do rim está melhorando ou piorando? As doses dos remédios estão ajustadas para esse valor?',
  },
  sodio: {
    oQueE: 'Um sal do sangue que controla a quantidade de água dentro e fora das células.',
    porQueImporta: 'Alterações grandes afetam o cérebro e podem causar confusão mental ou sonolência.',
    quandoAlto: 'Em geral sobra sal ou falta água.',
    quandoBaixo: 'Em geral sobra água em relação ao sal; é das alterações mais comuns em internação.',
    pergunta: 'O sódio fora da faixa precisa de correção? Tem a ver com o líquido que ela recebe?',
  },
  potassio: {
    oQueE: 'Um sal essencial para o batimento do coração e para a força dos músculos.',
    porQueImporta: 'Tanto o excesso quanto a falta podem alterar o ritmo do coração — por isso é repetido tantas vezes.',
    quandoAlto: 'Pode alterar o ritmo cardíaco; costuma ser corrigido logo.',
    quandoBaixo: 'Também altera ritmo e dá fraqueza muscular; é reposto por soro ou pela sonda.',
    pergunta: 'O potássio precisa de reposição? Isso tem relação com o ritmo do coração dela?',
  },
  magnesio: {
    oQueE: 'Um mineral que participa do ritmo do coração e do funcionamento dos músculos e nervos.',
    quandoBaixo: 'Costuma ser reposto, principalmente quando há arritmia.',
    pergunta: 'O magnésio precisa de ajuste? Ele influencia o ritmo do coração?',
  },
  calcio: {
    oQueE: 'Mineral do osso que também é essencial para a contração do coração e dos músculos.',
  },
  fosforo: {
    oQueE: 'Mineral ligado ao osso e à energia das células. O rim controla o nível dele.',
    pergunta: 'O fósforo baixo precisa de reposição? Tem a ver com a nutrição?',
  },
  tgo: {
    oQueE: 'Uma enzima presente no fígado e também no músculo e no coração.',
    quandoAlto: 'Indica que alguma dessas células foi agredida. Sozinha não diz qual.',
    pergunta: 'As enzimas do fígado subiram. Algum dos remédios em uso pode estar causando isso?',
  },
  tgp: {
    oQueE: 'Uma enzima mais específica do fígado que a TGO.',
    quandoAlto: 'Aponta mais diretamente para o fígado.',
    pergunta: 'As enzimas do fígado subiram. Algum dos remédios em uso pode estar causando isso?',
  },
  ggt: {
    oQueE: 'Uma enzima do fígado e dos canais que levam a bile. É a que diz de onde vem a fosfatase alcalina: se as duas sobem juntas, a origem é o fígado, não o osso.',
    quandoAlto: 'Sobe quando a bile tem dificuldade de sair, com vários remédios (antibióticos, amiodarona, anticonvulsivantes), com o fígado congestionado pela insuficiência cardíaca e na infecção grave.',
    pergunta: 'A Gama GT alta vem de algum remédio, do coração ou da infecção? Precisa de ultrassom de abdome se continuar subindo?',
  },
  fosfatase_alcalina: {
    oQueE: 'Uma enzima que vem sobretudo do fígado (dos canais que levam a bile) e dos ossos.',
    quandoAlto: 'Sobe quando a bile tem dificuldade de sair do fígado, e também com doença ou remodelação óssea. Sozinha, sem a bilirrubina alterada, costuma ser pouco específica.',
    pergunta: 'A fosfatase alcalina alta vem do fígado ou do osso? Vale repetir junto com a GGT?',
  },
  bilirrubina_total: {
    oQueE: 'Um pigmento amarelo que vem da reciclagem dos glóbulos vermelhos e é eliminado pelo fígado.',
    quandoAlto: 'Em níveis altos deixa a pele e os olhos amarelados (icterícia).',
    pergunta: 'A bilirrubina alterada indica alguma coisa no fígado ou na via biliar?',
  },
  bilirrubina_direta: {
    oQueE: 'A parte da bilirrubina que o fígado já processou.',
    quandoAlto: 'Aponta mais para dificuldade de eliminar pela via biliar.',
    pergunta: 'Há alguma dificuldade na via biliar, ou isso é reflexo de outra coisa?',
  },
  bilirrubina_indireta: {
    oQueE: 'A parte da bilirrubina que o fígado ainda não processou.',
  },
  albumina: {
    oQueE: 'A principal proteína do sangue, produzida pelo fígado. Segura o líquido dentro dos vasos.',
    porQueImporta: 'Reflete o estado nutricional e a gravidade de doenças prolongadas.',
    quandoBaixo: 'Comum em internação longa e em inflamação. Favorece inchaço, porque o líquido escapa para fora dos vasos.',
    pergunta: 'A nutrição dela está adequada? Vale avaliação da nutricionista?',
  },
  inr: {
    oQueE: 'Mede quanto tempo o sangue leva para coagular.',
    porQueImporta: 'Acompanha o risco de sangramento e a ação de anticoagulantes.',
    quandoAlto: 'O sangue está demorando mais para coagular que o normal.',
    pergunta: 'Este valor está onde deveria estar para o anticoagulante que ela usa? Há risco de sangramento?',
  },
  ttpa: {
    oQueE: 'Outra medida do tempo de coagulação, por um caminho diferente do INR.',
    pergunta: 'Este valor está na faixa esperada para quem usa anticoagulante? Precisa ajustar a dose?',
  },
  ph: {
    oQueE: 'Diz se o sangue está mais ácido ou mais alcalino que o normal.',
    porQueImporta: 'O corpo mantém isso numa faixa estreitíssima; sair dela afeta todos os órgãos.',
    quandoBaixo: 'Sangue mais ácido. Aparece quando falta oxigênio nos tecidos, em infecção grave ou em problema renal.',
    quandoAlto: 'Sangue mais alcalino. Pode vir de respiração acelerada ou de perdas pelo vômito.',
    pergunta: 'Este valor indica algum desequilíbrio que precise ser corrigido?',
  },
  pco2: {
    oQueE: 'A quantidade de gás carbônico no sangue — o "resíduo" que sai pelo pulmão.',
    porQueImporta: 'Mostra se a respiração está eliminando gás carbônico bem.',
    quandoAlto: 'A respiração não está dando conta de eliminar. Comum em pulmão comprometido.',
    quandoBaixo: 'Respiração acelerada, eliminando mais do que o normal.',
    pergunta: 'O que a gasometria diz sobre a respiração dela agora?',
  },
  po2: {
    oQueE: 'A quantidade de oxigênio dissolvida no sangue arterial.',
    quandoBaixo: 'Menos oxigênio chegando ao sangue do que o esperado.',
    pergunta: 'O que a gasometria diz sobre a oxigenação dela agora?',
  },
  hco3: {
    oQueE: 'Bicarbonato: a substância que o corpo usa para neutralizar o excesso de ácido.',
    quandoBaixo: 'O corpo está gastando bicarbonato para compensar acidez.',
    pergunta: 'Este resultado indica algum desequilíbrio que precise ser corrigido?',
  },
  be: {
    oQueE: 'Base excess: o quanto falta ou sobra de "antiácido" no sangue.',
    quandoBaixo: 'Número bem negativo indica acidez acumulada.',
    pergunta: 'Este resultado indica algum desequilíbrio que precise ser corrigido?',
  },
  lactato: {
    oQueE: 'Uma substância que os tecidos produzem quando não recebem oxigênio suficiente.',
    porQueImporta: 'É um dos marcadores mais usados de gravidade: sobe quando a circulação não está entregando oxigênio, e cai quando melhora.',
    quandoAlto: 'Indica que algum tecido esteve sem oxigênio adequado. Sobe muito em parada cardíaca e em choque.',
    pergunta: 'O lactato caiu depois da intercorrência? Isso indica que a circulação se recuperou?',
  },
  saturacao_arterial: {
    oQueE: 'A porcentagem dos glóbulos vermelhos que estão carregando oxigênio.',
  },
  svo2_central: {
    oQueE: 'Quanto oxigênio ainda sobra no sangue que volta dos órgãos para o coração.',
    porQueImporta: 'Ajuda a avaliar se a circulação está entregando oxigênio suficiente ao corpo.',
    quandoBaixo: 'Os tecidos estão extraindo mais oxigênio que o normal — sinal de que a entrega pode estar insuficiente.',
    pergunta: 'O que este valor indica sobre a oferta de oxigênio aos tecidos?',
  },
  gap_co2: {
    oQueE: 'A diferença de gás carbônico entre o sangue venoso e o arterial.',
    porQueImporta: 'Valores maiores sugerem que o sangue está circulando devagar pelos tecidos.',
    pergunta: 'Este valor diz alguma coisa sobre a circulação dela neste momento?',
  },
  cpk: {
    oQueE: 'Uma enzima que sai do músculo quando ele é lesado.',
    quandoAlto: 'Aparece depois de esforço muscular intenso, imobilidade prolongada, convulsão ou manobras de reanimação.',
    pergunta: 'O que está elevando a CPK dela? Vale repetir, já que o antibiótico em uso pede acompanhamento desse exame?',
  },
  glicemia_lab: {
    oQueE: 'A quantidade de açúcar no sangue, medida em laboratório.',
    porQueImporta: 'Em pessoa internada e grave, tanto o excesso quanto a falta pioram o quadro.',
    pergunta: 'A glicemia está oscilando muito? Isso muda alguma coisa na dieta ou exige medicação?',
  },
};

// ------------------------------------------------------------------ culturas

export const VERBETE_CULTURA: Verbete = {
  oQueE: 'Uma amostra (sangue, urina ou secreção) é colocada num meio onde bactérias crescem, para descobrir se há infecção e qual é a bactéria.',
  porQueImporta: 'É o exame que permite trocar o antibiótico "de amplo espectro" por um dirigido àquela bactéria específica. Demora dias porque a bactéria precisa crescer.',
  pergunta: 'A cultura já liberou resultado? O antibiótico atual cobre o que cresceu?',
};

export const VERBETES_MATERIAL: Record<string, Verbete> = {
  hemocultura: {
    oQueE: 'Cultura feita com sangue, para procurar bactéria circulando na corrente sanguínea.',
    porQueImporta: 'Costuma ser colhida em mais de uma amostra, de locais diferentes. Quando só uma cresce e a outra não, muitas vezes é contaminação da coleta, não infecção de verdade.',
    pergunta: 'Cresceu em quantas amostras? Isso é infecção ou contaminação da coleta?',
  },
  urocultura: {
    oQueE: 'Cultura da urina, para procurar infecção urinária.',
    porQueImporta: 'Em quem usa sonda vesical, encontrar bactéria na urina é comum e nem sempre significa infecção que precisa ser tratada.',
  },
  'secrecao traqueal': {
    oQueE: 'Cultura da secreção aspirada das vias respiratórias.',
    porQueImporta: 'Ajuda a identificar a bactéria da pneumonia, principalmente em quem está ou esteve no tubo de respiração.',
  },
};

// ------------------------------------------------------------- dispositivos

export const VERBETES_DISPOSITIVO: Record<string, Verbete> = {
  'cateter venoso central': {
    oQueE: 'Um cateter fino instalado numa veia grande, que permite dar vários remédios ao mesmo tempo e medir pressões internas.',
    porQueImporta: 'É essencial em paciente grave, mas cada dia com ele aumenta o risco de infecção na corrente sanguínea. Por isso a equipe reavalia com frequência se ainda é necessário.',
    pergunta: 'O cateter central ainda é necessário? Há quantos dias está?',
  },
  'marca-passo': {
    oQueE: 'Um aparelho que envia impulsos elétricos ao coração quando o batimento natural está lento demais ou falhando.',
    porQueImporta:
      'Há três formatos, e a diferença muda o dia a dia. O TRANSVENOSO provisório entra por uma veia do pescoço ou da virilha e prende a pessoa à cama. O EXTERNO provisório (Pacebox e semelhantes) tem o eletrodo no peito e o gerador do lado de fora, o que devolve mobilidade enquanto se espera. O DEFINITIVO fica sob a pele, numa cirurgia, e é o que encerra a espera. ' +
      'Enquanto qualquer um deles estiver instalado, a ressonância magnética costuma ficar contraindicada, porque o exame usa ímã e o aparelho tem metal.',
    alerta: 'PORTADORA DE MARCA-PASSO. Alguns exames e procedimentos mudam por causa disso — verificar antes de solicitar.',
    gravidade: 'critico',
    // Fontes: diretrizes de segurança em ressonância magnética e o material da
    // SOCERJ sobre interferência eletromagnética em marca-passo.
    //
    // A lista é CURTA de propósito, e esse é o critério para mexer nela: entra
    // só o que pode ser pedido para uma pessoa com este quadro. Litotripsia e
    // radioterapia também interferem com marca-passo e ficaram de fora — ela
    // não tem cálculo renal nem câncer, e um alerta que lista o improvável
    // dilui o que importa e faz o leitor desconfiar do resto. O médico que lê
    // isto em trinta segundos precisa dos quatro itens que ele de fato pode
    // solicitar, não de um tratado sobre o aparelho.
    restricoes: [
      {
        o: 'Ressonância magnética',
        porque: 'Contraindicada de rotina em portador de dispositivo cardíaco. Quando o exame é indispensável, exige aparelho compatível, reprogramação para modo assíncrono e protocolo próprio — não é exame que se peça de rotina.',
      },
      {
        o: 'Bisturi elétrico (eletrocautério) em cirurgia',
        porque: 'Preferir o bipolar. Sendo unipolar, a placa vai o mais longe possível do gerador e a corrente sai em pulsos curtos.',
      },
      {
        o: 'Diatermia, ondas curtas e TENS na fisioterapia',
        porque: 'Aplicam campo elétrico no tecido e interferem na leitura do aparelho — ou o danificam. A fisioterapia motora continua normal.',
      },
      {
        o: 'Cardioversão e desfibrilação',
        porque: 'Podem ser feitas, com as pás longe do gerador e conferência do aparelho depois.',
      },
    ],
    liberado: 'Tomografia, raio-X, ultrassom, ecocardiograma, endoscopia e exames de sangue seguem normais — o alerta não é motivo para adiá-los.',
    pergunta: 'Há previsão para o marca-passo definitivo? O que ainda falta para marcar a cirurgia?',
  },
  'tubo orotraqueal': {
    oQueE: 'Um tubo pela boca até a traqueia, ligado ao aparelho de respiração.',
    porQueImporta: 'Permite o ventilador respirar pela pessoa. Quanto mais tempo, maior o risco de pneumonia associada — por isso a equipe tenta retirar assim que for seguro.',
  },
  'sonda vesical': {
    oQueE: 'Uma sonda que drena a urina direto da bexiga.',
    porQueImporta: 'Permite medir a urina com precisão, o que mostra como o rim está respondendo. Também aumenta risco de infecção urinária com o tempo.',
  },
  'sonda nasoenteral': {
    oQueE: 'Uma sonda pelo nariz até o intestino, por onde entra a alimentação.',
    porQueImporta: 'Mantém a nutrição de quem não pode comer pela boca — e nutrição é parte do tratamento, não detalhe.',
    alerta: 'Alimentação por sonda. Não oferecer comida nem líquido pela boca sem liberação da equipe.',
    gravidade: 'cuidado',
  },
  'pressao arterial invasiva': {
    oQueE: 'Um cateter numa artéria que mede a pressão batimento a batimento, em vez de de hora em hora.',
    porQueImporta: 'Usado quando a pressão precisa ser vigiada de perto, e permite colher sangue sem nova picada.',
  },
};

// ------------------------------------------------- exames de imagem e outros

export const VERBETES_PROCEDIMENTO: Record<string, Verbete> = {
  tomografia: {
    oQueE: 'Um raio-X feito em fatias, que o computador junta para mostrar os órgãos por dentro.',
  },
  'ressonancia magnetica': {
    oQueE: 'Exame de imagem feito com ímã muito forte, sem radiação.',
    porQueImporta: 'Por usar ímã, metal dentro do corpo — como fios de marca-passo — pode impedir o exame.',
    pergunta: 'A ressonância pode ser feita com o marca-passo dela? Se não puder, existe outro exame que responda a mesma pergunta?',
  },
  ecocardiograma: {
    oQueE: 'Um ultrassom do coração, que mostra as válvulas e o quanto o músculo está bombeando.',
    porQueImporta: 'Dele sai a fração de ejeção (FEVE): a porcentagem do sangue que o coração consegue expulsar a cada batida. Perto de 60% é o esperado; quanto menor, mais fraco está o bombeamento.',
    pergunta: 'O que a fração de ejeção dela indica sobre a força do coração? Isso muda o tratamento?',
  },
  cateterismo: {
    oQueE: 'Um cateter chega até as artérias do coração e injeta contraste, para ver se alguma está entupida.',
    porQueImporta: 'Responde se a causa do problema é entupimento de artéria coronária.',
  },
  eletroencefalograma: {
    oQueE: 'Registra a atividade elétrica do cérebro por eletrodos na cabeça. Não dói e não usa radiação.',
    porQueImporta: 'Usado para investigar convulsão e nível de consciência.',
  },
  eletrocardiograma: {
    oQueE: 'Registra a atividade elétrica do coração, mostrando o ritmo dos batimentos.',
  },
};

const ACENTOS = /[̀-ͯ]/g;
const chaveDe = (texto: string) =>
  texto.trim().toLowerCase().normalize('NFD').replace(ACENTOS, '');

/** Acha o verbete pelo nome escrito, aceitando variações ("TC de tórax"). */
function porTrecho<T>(mapa: Record<string, T>, nome: string): T | undefined {
  const alvo = chaveDe(nome);
  for (const [termo, verbete] of Object.entries(mapa)) {
    if (alvo.includes(chaveDe(termo))) return verbete;
  }
  return undefined;
}

export const verbeteDoMarcador = (chave: string): Verbete | undefined =>
  VERBETES_MARCADOR[chave];

export function verbeteDaCultura(material: string): Verbete {
  return porTrecho(VERBETES_MATERIAL, material) ?? VERBETE_CULTURA;
}

export const verbeteDoDispositivo = (nome: string): Verbete | undefined =>
  porTrecho(VERBETES_DISPOSITIVO, nome);

export function verbeteDoProcedimento(nome: string): Verbete | undefined {
  const alvo = chaveDe(nome);
  // "TC de tórax" e "TC de crânio" são tomografia sem dizer a palavra.
  if (alvo.startsWith('tc ') || alvo.includes('tomografia')) return VERBETES_PROCEDIMENTO.tomografia;
  if (alvo.includes('eco')) return VERBETES_PROCEDIMENTO.ecocardiograma;
  if (alvo.includes('cate')) return VERBETES_PROCEDIMENTO.cateterismo;
  if (alvo.includes('eeg') || alvo.includes('eletroencefalo')) return VERBETES_PROCEDIMENTO.eletroencefalograma;
  if (alvo.includes('ecg') || alvo.includes('eletrocardio')) return VERBETES_PROCEDIMENTO.eletrocardiograma;
  return porTrecho(VERBETES_PROCEDIMENTO, nome);
}

/* ------------------------------------------------------------ medicações
 *
 * O sistema explicava exame, dispositivo e cultura — e não explicava o que ela
 * TOMA. A lacuna apareceu quando o antibiótico finalmente ganhou nome:
 * Daptomicina. Saber o nome não adianta se ninguém sabe o que perguntar sobre
 * ele.
 *
 * `exigeAcompanhamento` é a parte que faz diferença de verdade. Certos
 * remédios pedem um exame de controle em intervalo definido — não como conduta
 * sugerida por este sistema, mas como característica conhecida do medicamento,
 * do mesmo jeito que está na bula. O sistema compara com a última coleta que
 * ele tem e, se estiver vencida, a visita ganha uma pergunta com o número de
 * dias dentro.
 */
export interface VerbeteMedicacao {
  oQueE: string;
  porQueImporta?: string;
  /** Exame de controle que o remédio pede, e de quanto em quanto tempo. */
  exigeAcompanhamento?: { marcador: string; aCadaDias: number; porque: string };
  pergunta?: string;
  /**
   * Em que exame ele pode mexer, e para que lado — efeito conhecido do remédio
   * (bula, farmacologia), escrito como possibilidade. É o que a tela do marcador
   * mostra em "o que pode mexer neste valor" (pedido do Ilton, 27/09). Não diz
   * que ESTÁ mexendo: diz onde olhar.
   *
   * `[]` quer dizer "revisado e nada conhecido" — diferente de ausente, que é
   * "ainda não revisado" e faz o remédio aparecer em `remediosSemRevisao`
   * (base de correlações, revisão de 27/09).
   */
  mexeEm?: {
    marcador: string;
    efeito: string;
    fonte?: string;
    /** Só no exame do LABORATÓRIO (interferência no método) — não vale para a glicemia do dedo. */
    soNoLaboratorio?: boolean;
    /**
     * Só por este caminho. A vancomicina pela sonda quase não chega ao sangue:
     * mostrar nela o efeito da vancomicina na veia é erro (Ilton, 27/09).
     */
    via?: 'veia' | 'sonda';
  }[];
}

/** O caminho do remédio, pelo campo `via` ou pelo nome. null = não se sabe (vale qualquer efeito). */
export function caminhoDoRemedio(nome: string, via?: string | null): 'veia' | 'sonda' | null {
  const t = `${via ?? ''} ${nome}`.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (/sonda|oral|boca|enteral|\bsne\b|\bvo\b/.test(t)) return 'sonda';
  if (/endoven|intraven|\bev\b|\biv\b|na veia/.test(t)) return 'veia';
  return null;
}

/* Fontes dos efeitos em exame (base revisada em 27/09/2026). Bulas: FDA/DailyMed. */
const BULA = (nome: string) => `${nome}, bula (FDA/DailyMed)`;

export const VERBETES_MEDICACAO: Record<string, VerbeteMedicacao> = {
  daptomicina: {
    oQueE:
      'Um antibiótico endovenoso para bactérias Gram-positivas resistentes — o grupo do estafilococo. ' +
      'É reservado para quando os antibióticos mais comuns não servem.',
    porQueImporta:
      'O efeito adverso conhecido dela é no MÚSCULO: pode causar dor muscular e, mais raramente, ' +
      'lesão muscular. Por isso a bula pede acompanhar a CPK, que é a enzima que sobe quando o músculo ' +
      'sofre. Em quem tem o rim filtrando abaixo do normal, a recomendação é acompanhar com intervalo ' +
      'ainda menor.',
    exigeAcompanhamento: {
      marcador: 'cpk',
      aCadaDias: 7,
      porque: 'a daptomicina pede CPK pelo menos semanal, e mais frequente quando a função renal está reduzida',
    },
    pergunta: 'Ela tem sentido dor muscular ou fraqueza? Isso é o que se vigia com este antibiótico.',
    mexeEm: [
      { marcador: 'cpk', efeito: 'pode subir: lesão muscular é o efeito adverso conhecido dela — a bula pede CPK semanal', fonte: BULA('Cubicin (daptomicina)') },
      { marcador: 'tgo', efeito: 'pode subir junto com a CPK quando o músculo sofre (a TGO também sai do músculo)', fonte: 'Nathwani RA et al. Hepatology 2005;41:380' },
      { marcador: 'inr', efeito: 'pode alargar o INR/TAP NO TUBO, com alguns reagentes, sem mexer na coagulação de verdade — maior perto do pico da dose', fonte: 'Am J Health Syst Pharm 2018;75:269 (PMID 29472508); bula Cubicin' },
      { marcador: 'tap_tempo', efeito: 'pode alargar o TAP no tubo (interferência com o reagente), sem sangue mais ralo de verdade', fonte: 'Am J Health Syst Pharm 2018;75:269 (PMID 29472508)' },
      { marcador: 'eosinofilos', efeito: 'pode subir: a bula descreve pneumonia eosinofílica e reação com eosinofilia', fonte: BULA('Cubicin (daptomicina)') },
    ],
  },
  heparina: {
    oQueE: 'Um anticoagulante: deixa o sangue mais lento para coagular, prevenindo trombose em quem passa muito tempo na cama.',
    porQueImporta:
      'O outro lado da moeda é o sangramento. Por isso a equipe olha as plaquetas e sinais de sangramento — ' +
      'roxos que aparecem sozinhos, sangramento na gengiva, sangue na urina ou nas fezes.',
    pergunta: 'Apareceu algum roxo novo, sangramento de gengiva ou sangue na urina?',
    mexeEm: [
      { marcador: 'plaquetas', efeito: 'pode baixar: queda de mais da metade entre o 5º e o 10º dia de uso levanta a suspeita de plaquetopenia pela heparina (HIT)', fonte: 'Cuker A et al. ASH 2018 HIT guideline. Blood Adv 2018;2:3360' },
      { marcador: 'hemoglobina', efeito: 'pode cair se houver sangramento', fonte: BULA('Heparina sódica') },
      { marcador: 'potassio', efeito: 'pode subir — a heparina reduz a aldosterona; risco maior depois de 5 dias, com rim fraco ou junto de remédio que segura potássio (espironolactona)', fonte: BULA('Heparina sódica') },
      { marcador: 'tgo', efeito: 'pode subir: a bula registra elevação de TGO e TGP com heparina, que pede cautela na leitura', fonte: BULA('Heparina sódica') },
      { marcador: 'tgp', efeito: 'pode subir: a bula registra elevação de TGO e TGP com heparina', fonte: BULA('Heparina sódica') },
      { marcador: 'ttpa', efeito: 'alarga o TTPA — em dose de prevenção (subcutânea), pouco', fonte: BULA('Heparina sódica') },
    ],
  },
  insulina: {
    oQueE: 'Hormônio que faz o açúcar sair do sangue e entrar nas células.',
    porQueImporta:
      'Nem toda pessoa que recebe insulina no hospital tem diabetes: ela também é usada para controlar ' +
      'a glicemia que oscila por doença grave, por remédio ou por interrupção da dieta.',
    mexeEm: [
      { marcador: 'glicemia_lab', efeito: 'baixa — e cai mais se a dieta parar com a insulina agindo', fonte: BULA('Lantus (insulina glargina)') },
      { marcador: 'potassio', efeito: 'pode baixar: leva o potássio do sangue para dentro das células', fonte: BULA('Lantus (insulina glargina)') },
      { marcador: 'fosforo', efeito: 'pode baixar, pelo mesmo mecanismo', fonte: 'da Silva JSV et al. ASPEN refeeding consensus. Nutr Clin Pract 2020;35:178' },
    ],
  },
  'dieta enteral': {
    oQueE: 'A alimentação líquida completa que corre pela sonda, em bomba, com velocidade controlada em mL por hora.',
    porQueImporta:
      'Nutrição em internação longa é parte do tratamento, não detalhe: cicatrização, força muscular e defesa ' +
      'contra infecção dependem dela. A velocidade é ajustada conforme a pessoa tolera — se houver golfada, ' +
      'distensão da barriga ou parada do intestino, ela é reduzida.',
    pergunta: 'A quantidade de dieta por dia está atingindo o que a nutricionista calculou para ela?',
    mexeEm: [
      { marcador: 'glicemia_lab', efeito: 'sobe: a dieta corre o dia todo; o alvo em internado com dieta contínua é 140–180', fonte: 'ADA Standards of Care, seção 16 (Diabetes care in the hospital)' },
      { marcador: 'fosforo', efeito: 'pode cair ao começar ou aumentar a dieta depois de dias comendo pouco (síndrome de realimentação)', fonte: 'da Silva JSV et al. ASPEN refeeding consensus. Nutr Clin Pract 2020;35:178' },
      { marcador: 'potassio', efeito: 'pode cair na realimentação, junto com o fósforo e o magnésio', fonte: 'da Silva JSV et al. ASPEN refeeding consensus. Nutr Clin Pract 2020;35:178' },
      { marcador: 'magnesio', efeito: 'pode cair na realimentação', fonte: 'da Silva JSV et al. ASPEN refeeding consensus. Nutr Clin Pract 2020;35:178' },
      { marcador: 'albumina', efeito: 'não mede a dieta: cai com inflamação, mesmo bem nutrida', fonte: 'Evans DC et al. ASPEN position paper. Nutr Clin Pract 2021;36:22' },
      { marcador: 'ureia', efeito: 'fórmula rica em proteína (a "HP") eleva a ureia — sem ser sinal de rim pior', fonte: 'StatPearls: Azotemia (NBK538145)' },
      { marcador: 'sodio', efeito: 'as fórmulas trazem pouco sódio; com água livre pela sonda, o sódio tende a cair', fonte: 'Spasovski G et al. Eur J Endocrinol 2014;170:G1' },
    ],
  },
  racecadotrila: {
    oQueE:
      'Um remédio para diarreia que age de um jeito diferente do mais conhecido: em vez de deixar o ' +
      'intestino mais lento, ele reduz a quantidade de água e de sal que o intestino despeja para dentro ' +
      'de si. As fezes ficam menos líquidas sem que o trânsito seja travado.',
    porQueImporta:
      'Essa diferença importa quando a diarreia pode ser causada por bactéria: os remédios que travam o ' +
      'intestino seguram lá dentro o que o corpo está tentando expulsar, e por isso não são a primeira ' +
      'escolha nesse caso. O uso é CURTO, em geral até sete dias — segurar a diarreia não é o mesmo que ' +
      'descobrir a causa dela. Se vier febre, sangue nas fezes, barriga distendida ou piora, a equipe ' +
      'precisa saber na hora.',
    pergunta: 'Quantas evacuações por dia, e como estão? Vale pesquisar bactéria nas fezes, já que ela usou muitos antibióticos?',
    // Revisado: a bula não descreve efeito em exame de sangue. A diarreia que
    // ela trata é que mexe (sódio, potássio, ureia) — isso está nas correlações.
    mexeEm: [],
  },
  pantoprazol: {
    oQueE: 'Reduz a produção de ácido no estômago — o que se chama de protetor gástrico.',
    porQueImporta:
      'Entra para proteger o estômago em quem está internado e grave, ou para tratar úlcera e refluxo. ' +
      'Usado por muito tempo, ele tem contrapartidas conhecidas: mais risco de infecção intestinal e de ' +
      'pneumonia, e menos absorção de magnésio e de vitamina B12. Por isso a equipe costuma revisar de ' +
      'tempos em tempos se ele ainda é necessário.',
    pergunta: 'Por que ela começou o protetor gástrico, e até quando ele vai?',
    mexeEm: [
      { marcador: 'magnesio', efeito: 'pode baixar com o uso prolongado (menos absorção no intestino)', fonte: BULA('Protonix (pantoprazol)') },
      { marcador: 'vitamina_b12', efeito: 'pode baixar com uso longo (anos): o ácido ajuda a soltar a B12 da comida', fonte: BULA('Protonix (pantoprazol)') },
      { marcador: 'sodio', efeito: 'raramente baixa (hiponatremia descrita com os protetores gástricos)', fonte: BULA('Protonix (pantoprazol)') },
    ],
  },
  dipirona: {
    oQueE: 'Remédio para dor e para febre. Prescrito "se necessário": só é dado quando aparece dor ou febre.',
    porQueImporta:
      'Por isso cada dose conta uma história — se ela precisou de dipirona, teve dor ou febre naquela hora, ' +
      'e febre em quem trata infecção é informação para a equipe. Na veia, se correr rápido, pode baixar a ' +
      'pressão; raramente causa alergia ou queda dos glóbulos brancos.',
    pergunta: 'Ela precisou de dipirona hoje? Foi por febre ou por dor?',
    mexeEm: [
      { marcador: 'leucocitos', efeito: 'raramente derruba os glóbulos brancos (agranulocitose) — reação rara, mas conhecida', fonte: 'Dipirona sódica, bula ANVISA — agranulocitose' },
      { marcador: 'segmentados', efeito: 'são os neutrófilos, os primeiros a cair na agranulocitose', fonte: 'Dipirona sódica, bula ANVISA — agranulocitose' },
    ],
  },
  dapagliflozina: {
    oQueE:
      'Um remédio do grupo das "gliflozinas": faz o rim eliminar açúcar e um pouco de sal pela urina. ' +
      'Nasceu para diabetes, mas hoje é um dos pilares do tratamento da insuficiência cardíaca com o coração ' +
      'bombeando pouco, mesmo em quem não é diabético — reduz internações e mortes.',
    porQueImporta:
      'Por fazer perder água e açúcar pela urina, ele pede cuidado com desidratação, pressão baixa e infecção ' +
      'urinária. Em doença aguda, com pouca alimentação ou vômitos, muitas equipes suspendem por uns dias. A dose ' +
      'para o coração é de 10 mg uma vez ao dia.',
    exigeAcompanhamento: {
      marcador: 'creatinina',
      aCadaDias: 7,
      porque: 'a dapagliflozina mexe com o rim e com a água do corpo nas primeiras semanas',
    },
    pergunta: 'Ela está recebendo UM comprimido de 10 mg por dia? E com a urina alterada e a infecção recente, é hora de manter?',
    mexeEm: [
      { marcador: 'creatinina', efeito: 'costuma subir um pouco nas primeiras semanas (efeito esperado) — e sobe mais com desidratação', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'tfg', efeito: 'cai um pouco no início, de forma esperada, e depois se estabiliza', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'sodio', efeito: 'faz perder sal e água pela urina — efeito pequeno, mas soma com diarreia e outros diuréticos', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'glicemia_lab', efeito: 'baixa um pouco; sozinha raramente causa hipoglicemia, mas soma com a insulina', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'hematocrito', efeito: 'sobe um pouco (perde água e estimula a produção de glóbulos vermelhos) — subida pequena é esperada', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'hemoglobina', efeito: 'sobe um pouco, pelo mesmo motivo do hematócrito', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'fosforo', efeito: 'sobe um pouco (efeito descrito na bula)', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'ph', efeito: 'cetoacidose com glicose QUASE NORMAL é o risco sério: jejum, doença aguda e cirurgia aumentam; a bula pede suspender pelo menos 3 dias antes de cirurgia', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'hco3', efeito: 'cai na cetoacidose, que com gliflozina pode vir com glicose quase normal (jejum e cirurgia aumentam o risco)', fonte: BULA('Farxiga (dapagliflozina)') },
      { marcador: 'anion_gap', efeito: 'sobe na cetoacidose — sinal que não depende da glicose estar alta', fonte: BULA('Farxiga (dapagliflozina)') },
    ],
  },
  espironolactona: {
    oQueE:
      'Um diurético leve que também protege o coração: faz parte do tratamento da insuficiência cardíaca com ' +
      'fração de ejeção baixa. Diferente dos outros diuréticos, ela SEGURA potássio no corpo.',
    porQueImporta:
      'Por segurar potássio, a combinação com reposição de potássio pede exame de sangue frequente: o potássio ' +
      'alto demais mexe com o ritmo do coração. O rim também precisa ser acompanhado.',
    exigeAcompanhamento: {
      marcador: 'potassio',
      aCadaDias: 3,
      porque: 'a espironolactona segura potássio, e junto com reposição de potássio ele pode subir demais',
    },
    pergunta: 'Com a espironolactona, ela ainda precisa do cloreto de potássio? Quando repetem o potássio?',
    mexeEm: [
      { marcador: 'potassio', efeito: 'sobe: ela segura potássio no corpo — soma com reposição de potássio e com heparina', fonte: BULA('Aldactone (espironolactona)') },
      { marcador: 'sodio', efeito: 'pode baixar: faz perder sal pela urina', fonte: BULA('Aldactone (espironolactona)') },
      { marcador: 'creatinina', efeito: 'pode subir, sobretudo com desidratação', fonte: BULA('Aldactone (espironolactona)') },
    ],
  },
  'cloreto de potassio': {
    oQueE: 'Reposição de potássio, um sal que o coração e os músculos usam para funcionar.',
    porQueImporta:
      'Serve para corrigir potássio baixo. O cuidado é não passar do ponto — ainda mais junto com remédios que ' +
      'seguram potássio, como a espironolactona.',
    pergunta: 'O potássio já está normal: a reposição continua por quanto tempo?',
    mexeEm: [
      { marcador: 'potassio', efeito: 'sobe: é para isso que ele é dado — junto com espironolactona, sobe mais', fonte: BULA('Cloreto de potássio') },
    ],
  },
  ondansetrona: {
    oQueE: 'Um remédio contra enjoo e vômito.',
    porQueImporta:
      'Está prescrito "se necessário", para quando houver náusea. Em algumas pessoas pode alterar o ritmo do ' +
      'coração e deixar o intestino preso, e por isso não é dado de rotina.',
    pergunta: 'Ela precisou do remédio de enjoo? Quantas vezes?',
    // Revisado: o efeito vigiado é no eletrocardiograma (QT), não em exame de
    // sangue. Potássio e magnésio baixos aumentam esse risco — mas isso é deles.
    mexeEm: [],
  },
  metronidazol: {
    oQueE:
      'Um antibiótico para bactérias que vivem sem oxigênio — as chamadas anaeróbias, que moram sobretudo ' +
      'no intestino. NÃO é antifúngico: não trata fungo.',
    porQueImporta:
      'Junto com a vancomicina dada pela sonda, é o esquema usado contra a bactéria Clostridioides difficile, ' +
      'que causa diarreia depois de um tempo de antibiótico. Enquanto ele corre, gosto metálico na boca e ' +
      'enjoo são comuns. Bebida alcoólica não pode, nem durante nem nos dias seguintes.',
    pergunta: 'O Clostridioides difficile chegou a ser pesquisado nas fezes, ou o tratamento começou pelo quadro?',
    mexeEm: [
      { marcador: 'tgo', efeito: 'pode sair FALSAMENTE BAIXA, até zero, em alguns métodos de laboratório (interferência na leitura, não no fígado)', fonte: BULA('Flagyl (metronidazol)') },
      { marcador: 'tgp', efeito: 'pode sair FALSAMENTE BAIXA, até zero, em alguns métodos de laboratório (interferência na leitura)', fonte: BULA('Flagyl (metronidazol)') },
      { marcador: 'glicemia_lab', efeito: 'a glicose do laboratório pelo método da hexoquinase pode sair falsamente baixa (interferência na leitura; o glicosímetro do dedo usa outro método)', fonte: BULA('Flagyl (metronidazol)'), soNoLaboratorio: true },
      { marcador: 'leucocitos', efeito: 'pode baixar um pouco (leucopenia leve, que volta ao parar)', fonte: BULA('Flagyl (metronidazol)') },
    ],
  },
  vancomicina: {
    oQueE:
      'Um antibiótico forte contra bactérias Gram-positivas, o grupo do estafilococo. O que ele faz depende ' +
      'do caminho: NA VEIA ele trata infecção no sangue e no corpo todo; PELA SONDA, quase não é absorvido e ' +
      'age dentro do intestino.',
    porQueImporta:
      'Por isso o caminho importa tanto quanto o nome. Pela sonda, é o tratamento de escolha para a bactéria ' +
      'Clostridioides difficile — fica onde a bactéria está e quase não passa para o sangue. Na veia, o que ' +
      'se vigia é o rim e a audição, e costuma-se medir o nível dela no sangue.',
    pergunta: 'A vancomicina de agora está indo pela sonda ou na veia? E qual a dose e de quantas em quantas horas?',
    mexeEm: [
      { marcador: 'creatinina', via: 'veia', efeito: 'na veia pode lesar o rim — por isso se acompanha a creatinina e o nível dela no sangue', fonte: BULA('Vancomicina injetável') },
      { marcador: 'creatinina', via: 'sonda', efeito: 'pela sonda quase não é absorvida; só com colite e rim fraco pode chegar ao sangue, e aí a bula sugere dosar o nível', fonte: BULA('Vancocin (vancomicina oral)') },
      { marcador: 'leucocitos', via: 'veia', efeito: 'na veia, por mais de uma semana, pode baixar os neutrófilos', fonte: BULA('Vancomicina injetável') },
    ],
  },
  'agua pela sonda': {
    oQueE: 'Água dada pela sonda, separada da dieta, para hidratar.',
    porQueImporta: 'É água sem sal: ajuda a ureia e a diarreia, mas em excesso dilui o sódio do sangue.',
    mexeEm: [
      { marcador: 'sodio', efeito: 'dilui: mais água sem sal tende a baixar o sódio', fonte: 'Spasovski G et al. Eur J Endocrinol 2014;170:G1' },
      { marcador: 'ureia', efeito: 'ajuda a baixar, quando a ureia alta vem de pouca água', fonte: 'StatPearls: Azotemia (NBK538145)' },
    ],
  },
  amiodarona: {
    oQueE: 'Remédio para arritmia (batimento irregular ou acelerado).',
    porQueImporta: 'Tem muito iodo e fica semanas no corpo depois de suspensa: pode alterar a tireoide e o fígado mesmo depois de parar.',
    mexeEm: [
      { marcador: 'tsh', efeito: 'pode subir ou cair, semanas depois — a tireoide sente o iodo', fonte: 'J Clin Endocrinol Metab 2021;106:226 (amiodarona e tireoide)' },
      { marcador: 't4_livre', efeito: 'costuma subir um pouco nos primeiros meses, sem ser hipertireoidismo', fonte: 'J Clin Endocrinol Metab 2021;106:226 (amiodarona e tireoide)' },
      { marcador: 'tgo', efeito: 'pode subir (efeito no fígado)', fonte: BULA('Cordarone (amiodarona)') },
      { marcador: 'tgp', efeito: 'pode subir (efeito no fígado)', fonte: BULA('Cordarone (amiodarona)') },
    ],
  },
  linezolida: {
    oQueE: 'Antibiótico contra bactérias Gram-positivas resistentes, na veia ou pela boca.',
    porQueImporta: 'Usado por mais de 10 a 14 dias, pode derrubar as células do sangue.',
    mexeEm: [
      { marcador: 'plaquetas', efeito: 'pode baixar, sobretudo depois de 10 a 14 dias de uso', fonte: BULA('Zyvox (linezolida)') },
      { marcador: 'hemoglobina', efeito: 'pode baixar com uso prolongado', fonte: BULA('Zyvox (linezolida)') },
      { marcador: 'sodio', efeito: 'pode baixar (hiponatremia / SIADH) — a bula pede vigiar o sódio em idosos e em quem usa diurético', fonte: BULA('Zyvox (linezolida)') },
      { marcador: 'lactato', efeito: 'pode subir (acidose láctica), com náusea e vômitos repetidos', fonte: BULA('Zyvox (linezolida)') },
      { marcador: 'glicemia_lab', efeito: 'pode baixar em quem usa insulina (hipoglicemia descrita na bula)', fonte: BULA('Zyvox (linezolida)') },
    ],
  },
  ampicilina: {
    oQueE: 'Antibiótico da família da penicilina, com um protetor (sulbactam) que impede algumas bactérias de destruí-lo.',
    porQueImporta: 'Cobre bactérias comuns da boca, da pele e do intestino — é escolha frequente em pneumonia por aspiração.',
    mexeEm: [
      { marcador: 'tgo', efeito: 'pode subir de forma passageira', fonte: BULA('Unasyn (ampicilina/sulbactam)') },
      { marcador: 'tgp', efeito: 'pode subir de forma passageira', fonte: BULA('Unasyn (ampicilina/sulbactam)') },
      { marcador: 'eosinofilos', efeito: 'pode subir (reação alérgica)', fonte: BULA('Unasyn (ampicilina/sulbactam)') },
    ],
  },
  enalapril: {
    oQueE: 'Remédio para pressão e para o coração (inibidor da ECA).',
    porQueImporta: 'Reduz a pressão e o esforço do coração; mexe com o potássio e com o rim, que por isso são acompanhados.',
    mexeEm: [
      { marcador: 'potassio', efeito: 'pode subir (reduz a aldosterona) — soma com espironolactona', fonte: BULA('Vasotec (enalapril)') },
      { marcador: 'creatinina', efeito: 'pode subir um pouco no início, e mais com desidratação', fonte: BULA('Vasotec (enalapril)') },
    ],
  },
  meropenem: {
    oQueE: 'Antibiótico de amplo espectro (carbapenêmico), usado em infecção grave.',
    porQueImporta: 'Reservado para infecção grave ou bactéria resistente.',
    mexeEm: [
      { marcador: 'tgo', efeito: 'pode subir de forma passageira', fonte: BULA('Merrem (meropenem)') },
      { marcador: 'tgp', efeito: 'pode subir de forma passageira', fonte: BULA('Merrem (meropenem)') },
      { marcador: 'plaquetas', efeito: 'pode subir ou baixar', fonte: BULA('Merrem (meropenem)') },
    ],
  },
  amicacina: {
    oQueE: 'Antibiótico da família dos aminoglicosídeos, na veia.',
    porQueImporta: 'Os efeitos conhecidos são no rim e na audição; por isso costuma-se dosar o nível dela no sangue.',
    mexeEm: [
      { marcador: 'creatinina', efeito: 'pode subir (lesão do rim), às vezes dias depois de parar', fonte: BULA('Amicacina') },
      { marcador: 'magnesio', efeito: 'pode baixar: o rim lesado perde magnésio', fonte: BULA('Amicacina') },
      { marcador: 'potassio', efeito: 'pode baixar, pelo mesmo motivo', fonte: BULA('Amicacina') },
    ],
  },
  polimixina: {
    oQueE: 'Antibiótico antigo, reservado para bactérias resistentes a quase tudo.',
    porQueImporta: 'O efeito conhecido é no rim, que é acompanhado durante todo o uso.',
    mexeEm: [
      { marcador: 'creatinina', efeito: 'pode subir (lesão do rim é o efeito mais comum)', fonte: BULA('Polimixina B') },
      { marcador: 'ureia', efeito: 'sobe junto com a creatinina quando o rim é lesado', fonte: BULA('Polimixina B') },
    ],
  },
};

export const verbeteDaMedicacao = (nome: string): VerbeteMedicacao | undefined =>
  porTrecho(VERBETES_MEDICACAO, nome);

/**
 * O aviso que acompanha toda explicação, sem exceção. Repetido na tela em vez
 * de ficar só na documentação: quem lê às três da manhã não leu a documentação.
 */
export const RODAPE_EXPLICACAO =
  'Isto explica o que a medida é, em geral. O que ela significa no caso dela ' +
  'depende do exame e do contexto — quem responde isso é a equipe.';

// ------------------------------------------- lendo o RESULTADO, não só o exame
//
// Saber "o que é hemocultura" ajuda pouco quando o card diz "S. epidermidis,
// colônia não quantificada, possível contaminação". O que a família precisa é
// do significado DAQUELE resultado. Isto não é IA: são padrões conhecidos de
// microbiologia, reconhecidos no texto que a própria equipe escreveu.

export interface Bacteria {
  nome: string;
  ondeVive: string;
  observacao?: string;
}

const BACTERIAS: Record<string, Bacteria> = {
  'staphylococcus epidermidis': {
    nome: 'Staphylococcus epidermidis',
    ondeVive: 'Vive normalmente na pele de todo mundo.',
    observacao:
      'Justamente por viver na pele, é a bactéria que mais entra na amostra durante a coleta sem estar ' +
      'no sangue. Quando cresce em uma única amostra e sem contagem, costuma ser contaminação, não infecção.',
  },
  'staphylococcus aureus': {
    nome: 'Staphylococcus aureus',
    ondeVive: 'Vive na pele e no nariz, mas é agressiva quando chega ao lugar errado.',
    observacao: 'Diferente da epidermidis, raramente é descartada como contaminação: encontrá-la é levado a sério.',
  },
  mssa: {
    nome: 'Staphylococcus aureus sensível à oxacilina (MSSA)',
    ondeVive: 'Vive na pele e no nariz.',
    observacao: 'O "MS" quer dizer que ela ainda responde aos antibióticos mais simples — é a forma menos resistente.',
  },
  mrsa: {
    nome: 'Staphylococcus aureus resistente à oxacilina (MRSA)',
    ondeVive: 'Vive na pele e no nariz.',
    observacao: 'O "MR" quer dizer resistente aos antibióticos mais comuns, o que exige outros mais fortes.',
  },
  klebsiella: {
    nome: 'Klebsiella pneumoniae',
    ondeVive: 'Vive no intestino, mas causa pneumonia e infecção urinária quando chega a outros lugares.',
    observacao:
      'É das bactérias mais associadas a infecção adquirida dentro do hospital, e com frequência resistente ' +
      'a vários antibióticos — por isso o antibiograma pesa tanto aqui.',
  },
  'escherichia coli': {
    nome: 'Escherichia coli',
    ondeVive: 'Vive no intestino; é a causa mais comum de infecção urinária.',
  },
  pseudomonas: {
    nome: 'Pseudomonas aeruginosa',
    ondeVive: 'Vive em ambientes úmidos, inclusive em equipamento hospitalar.',
    observacao: 'Costuma ser resistente a vários antibióticos.',
  },
  enterococcus: { nome: 'Enterococcus', ondeVive: 'Vive no intestino.' },
  candida: {
    nome: 'Candida',
    ondeVive: 'É um fungo, não bactéria. Vive na boca e no intestino.',
    observacao: 'Antibiótico não trata fungo — o tratamento é de outra classe.',
  },
};

export interface LeituraDeCultura {
  situacao: string;
  texto: string;
  pergunta: string;
  /** Quanto isso pesa — a tela usa para escolher a cor. */
  peso: 'aguardando' | 'tranquilizador' | 'atencao' | 'neutro';
}

const semAcento = (t: string) => t.toLowerCase().normalize('NFD').replace(ACENTOS, '');

function acharBacteria(texto: string): Bacteria | undefined {
  const alvo = semAcento(texto);
  for (const [termo, b] of Object.entries(BACTERIAS)) {
    if (alvo.includes(termo)) return b;
  }
  // No prontuário elas aparecem abreviadas: "S. epidermidis", "E. coli".
  if (/\bs\.?\s*epidermidis/.test(alvo)) return BACTERIAS['staphylococcus epidermidis'];
  if (/\bs\.?\s*aureus/.test(alvo)) return BACTERIAS['staphylococcus aureus'];
  if (/\be\.?\s*coli/.test(alvo)) return BACTERIAS['escherichia coli'];
  return undefined;
}

/**
 * Lê o resultado que a equipe escreveu e explica o que ele significa.
 *
 * `paciente` entra no texto para a leitura soar como conversa sobre uma pessoa,
 * e não como boletim. Vem do apelido guardado no banco — o código não sabe nome
 * nenhum, e continua não sabendo.
 */
export function lerResultadoDeCultura(
  material: string,
  resultado: string | null,
  paciente = 'ela',
): LeituraDeCultura {
  const mat = semAcento(material);
  const ondeFoi = mat.includes('hemocultura') || mat.includes('sangue')
    ? 'no sangue'
    : mat.includes('uro') || mat.includes('urina')
      ? 'na urina'
      : mat.includes('traqueal') || mat.includes('secre')
        ? 'na secreção do pulmão'
        : 'no material coletado';

  if (!resultado?.trim()) {
    return {
      situacao: 'Ainda sem resultado',
      peso: 'aguardando',
      texto:
        'Esta cultura foi colhida e ainda está no laboratório. A bactéria precisa de dias para crescer — ' +
        'não é demora de papelada. Enquanto o resultado não sai, o antibiótico costuma ser escolhido para ' +
        'cobrir as bactérias mais prováveis, e depois ajustado quando o exame aponta qual é.',
      pergunta: 'Já saiu alguma parcial dessa cultura? Quando ela é esperada?',
    };
  }

  const texto = semAcento(resultado);

  if (texto.includes('negativa') || texto.includes('negativo') || texto.includes('ausencia de crescimento')) {
    return {
      situacao: 'Não cresceu nada',
      peso: 'tranquilizador',
      texto:
        `Nada cresceu nesta amostra. É boa notícia: naquela coleta, o exame não encontrou bactéria ${ondeFoi} ` +
        `de ${paciente}. Não garante que não haja infecção em outro lugar, nem depois — a cultura mostra o ` +
        'retrato daquele momento, não o corpo inteiro nem o de hoje.',
      pergunta: 'Com essa cultura negativa, o antibiótico ainda é necessário?',
    };
  }

  const bacteria = acharBacteria(resultado);
  const suspeitaContaminacao =
    texto.includes('contamina') || texto.includes('nao quantificada') || texto.includes('nao quantificado');

  if (suspeitaContaminacao) {
    return {
      situacao: 'Cresceu, mas pode ter sido contaminação da coleta',
      peso: 'neutro',
      texto:
        `Cresceu ${bacteria?.nome ?? 'uma bactéria'} nesta amostra, e a própria equipe levantou a hipótese de ` +
        `contaminação — ou seja, de ela ter entrado na amostra na hora de colher, em vez de estar ${ondeFoi} ` +
        `de ${paciente}. ${bacteria?.ondeVive ?? ''} ${bacteria?.observacao ?? ''} ` +
        'É por isso que a hemocultura costuma ser colhida em mais de um local: quando só uma amostra cresce, ' +
        'a suspeita de contaminação aumenta; quando várias crescem com a mesma bactéria, a infecção fica provável.',
      pergunta: 'Essa cultura foi considerada contaminação ou infecção de verdade? Foi colhida outra amostra?',
    };
  }

  const sensiveis = /sens[ií]vel\s*(?:a|à|aos|às)?\s*([^).;]+)/i.exec(resultado)?.[1]?.trim();
  const contagem = /(\d[\d.,]*)\s*ufc/i.exec(resultado)?.[1];

  // Bactéria produtora de carbapenemase (30/09): a urocultura de 21/09 trouxe
  // KPC e NDM, e a folha só dizia "cresceu Klebsiella". Isso muda tudo — é
  // multirresistente, pede CCIH e precaução de contato (ANVISA NT 01/2013; MS
  // NT 2022) — e precisa estar escrito.
  const enzimas = ['KPC', 'NDM', 'OXA-48', 'VIM', 'IMP']
    .filter((e) => new RegExp(`\\b${e.replace('-', '-?')}\\b`, 'i').test(resultado));
  if (enzimas.length > 0 || texto.includes('carbapenemase')) {
    const quais = enzimas.length > 0 ? enzimas.join(' e ') : 'carbapenemase';
    const todas = /resistente a todos|pan-?resist/.test(texto);
    const sinergia = /sinergismo\s+([^.;]+?)\s+positivo/i.exec(resultado)?.[1]?.trim();
    const naUrina = ondeFoi === 'na urina';
    return {
      situacao: `Cresceu ${bacteria?.nome ?? 'bactéria'} multirresistente (${quais})`,
      peso: 'atencao',
      texto:
        `Cresceu ${bacteria?.nome ?? 'uma bactéria'} ${ondeFoi} de ${paciente}` +
        `${contagem ? `, em quantidade de ${contagem} UFC/mL` : ''}. ` +
        `Ela produz ${quais} — enzimas chamadas carbapenemases, que destroem os carbapenêmicos (como o meropenem), ` +
        'os antibióticos mais fortes de uso comum. ' +
        (todas ? 'No antibiograma, resistiu a todos os antibióticos testados. ' : '') +
        (sinergia ? `O laboratório apontou ação apenas na combinação ${sinergia}. ` : '') +
        'É uma bactéria multirresistente: a ANVISA e o Ministério da Saúde orientam comunicar a Comissão de ' +
        'Controle de Infecção (CCIH) e manter precaução de contato (avental e luvas) para que ela não passe a ' +
        'outros pacientes. ' +
        (naUrina
          ? 'Na urina, principalmente com sonda, a bactéria pode estar só presente, sem causar infecção ' +
            '(colonização); sem sintomas urinários, em geral não se trata. A definição — infecção ou ' +
            'colonização — cabe à equipe com a CCIH, e precisa ficar registrada. '
          : '') +
        'Em quem tem ou vai receber um aparelho dentro do corpo, como um marca-passo, uma infecção por ela ' +
        'teria pouquíssimas opções de tratamento.',
      pergunta: 'A CCIH foi comunicada? É infecção ou colonização — e isso está registrado? Ela está em precaução de contato?',
    };
  }

  return {
    situacao: `Cresceu ${bacteria?.nome ?? 'bactéria'}`,
    peso: 'atencao',
    texto:
      `Cresceu ${bacteria?.nome ?? 'uma bactéria'} ${ondeFoi} de ${paciente}` +
      `${contagem ? `, em quantidade de ${contagem} UFC/mL` : ''}. ` +
      `${bacteria?.ondeVive ?? ''} ${bacteria?.observacao ?? ''} ` +
      (sensiveis
        ? `O laboratório testou quais antibióticos funcionam contra ela e apontou: ${sensiveis}. ` +
          'Isso permite trocar um antibiótico de amplo espectro por um dirigido, o que costuma ser melhor: ' +
          'agride menos o corpo e cria menos resistência.'
        : 'O antibiograma é a parte do exame que diz quais antibióticos funcionam contra ela; vale conferir se já saiu.'),
    pergunta: sensiveis
      ? 'O antibiótico que ela está tomando está entre os que funcionam contra essa bactéria?'
      : 'Já saiu o antibiograma dessa cultura? O antibiótico atual cobre essa bactéria?',
  };
}

/** Explica o valor de laboratório junto do fato de estar acima ou abaixo da faixa. */
/**
 * A explicacao de um exame, com a FAIXA escrita por extenso.
 *
 * Antes dizia so "o valor esta acima da faixa usual" — e o Ilton apontou o
 * buraco: alto em relacao a que? O laudo do laboratorio traz o intervalo ao
 * lado de cada numero justamente porque o numero sozinho nao significa nada
 * para quem le. A explicacao aqui passou a trazer o mesmo: o intervalo
 * considerado normal, o valor dela, e de que lado ele caiu.
 *
 * `valor` e opcional para nao quebrar quem chama sem ele — mas quem tem o
 * valor deve passar, porque e ele que transforma "esta alto" em informacao.
 */
export function lerValorDeExame(
  chave: string,
  situacao: 'alto' | 'baixo' | 'dentro' | 'desconhecido',
  paciente = 'ela',
  valor?: number,
): { texto: string; pergunta?: string; faixa?: string } | null {
  const v = VERBETES_MARCADOR[chave];
  if (!v) return null;

  const marcador = acharMarcador(chave);
  const ref = marcador?.referencia;
  const unidade = marcador?.unidade ? ` ${marcador.unidade}` : '';
  // A faixa usa as casas do proprio marcador: com arredondamento de uma casa,
  // o pH normal (7,35 a 7,45) viraria "7,4 a 7,5", que e outro intervalo.
  const faixa = ref
    ? `de ${formatarValor(chave, ref.min)} a ${formatarValor(chave, ref.max)}${unidade}`
    : undefined;
  const dela = valor === undefined ? null : `${formatarValor(chave, valor)}${unidade}`;

  const partes = [v.oQueE];
  if (v.porQueImporta) partes.push(v.porQueImporta);

  // De que lado o resultado caiu — dito SEMPRE, com faixa ou sem, com valor ou
  // sem. Era o que faltava quando a chamada não trazia o valor: a explicação
  // ficava só com o intervalo e deixava de dizer o principal.
  const onde = situacao === 'alto' ? 'acima da faixa'
    : situacao === 'baixo' ? 'abaixo da faixa'
      : situacao === 'dentro' ? 'dentro da faixa' : null;

  if (faixa) partes.push(`Na literatura, a faixa considerada normal para adulto vai ${faixa}.`);
  if (onde) {
    partes.push(dela
      ? `O valor de ${paciente} é ${dela}, ${onde}.`
      : `O valor de ${paciente} está ${onde}${faixa ? '' : ' usual'}.`);
  }

  // O que aquele lado significa vem DEPOIS do numero: primeiro o fato, depois
  // a leitura dele.
  if (situacao === 'alto' && v.quandoAlto) partes.push(v.quandoAlto);
  else if (situacao === 'baixo' && v.quandoBaixo) partes.push(v.quandoBaixo);

  return { texto: partes.join(' '), pergunta: v.pergunta, faixa };
}
